import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { trackConversion } from '../utils/analytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Check, 
  Zap, 
  ArrowLeft,
  Loader2,
  Crown,
  AlertTriangle,
  Tag,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    loadSubscription();
    // Track pricing page view
    trackConversion.pricingViewed();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`);
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountLoading(true);
    try {
      const planId = billingInterval === 'yearly' ? 'pro_yearly' : 'pro_monthly';
      const response = await axios.post(`${API}/billing/validate-discount`, {
        code: discountCode.trim(),
        plan_id: planId
      });
      
      setAppliedDiscount(response.data);
      toast.success(`Discount applied: ${response.data.description}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  // Re-validate discount when billing interval changes
  useEffect(() => {
    if (appliedDiscount && discountCode) {
      validateDiscountCode();
    }
  }, [billingInterval]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // Calculate price for tracking
    const price = planId === 'pro_yearly' 
      ? (appliedDiscount?.valid ? appliedDiscount.discounted_price : 190)
      : (appliedDiscount?.valid ? appliedDiscount.discounted_price : 19);
    
    // Track begin checkout
    trackConversion.beginCheckout(planId, price);

    setLoading(true);
    try {
      // Create checkout session with origin URL for redirects
      const payload = {
        plan_id: planId,
        origin_url: window.location.origin
      };
      
      // Include discount code if applied
      if (appliedDiscount?.valid) {
        payload.discount_code = appliedDiscount.code;
      }
      
      const response = await axios.post(`${API}/billing/create-checkout`, payload);

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/billing/cancel`, {});
      toast.success('Subscription will be cancelled at the end of your billing period');
      loadSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
      setShowCancelDialog(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/billing/reactivate`);
      toast.success('Subscription reactivated!');
      loadSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setLoading(false);
    }
  };

  const isSubscribed = subscription?.status === 'active';
  const isCancelled = subscription?.cancel_at_period_end;

  const features = [
    'Unlimited conversations',
    'All thinking modes (General, Emails, Ideas, Projects)',
    'Unlimited memory storage',
    'Knowledge base (100MB)',
    'Voice input transcription',
    'Conversation export',
    'Priority support'
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/chat')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Arlo</span>
          </button>
          
          <div className="relative inline-block">
            <div className="absolute -top-0.5 left-0">
              <div className="bg-white text-background px-0.5 py-0.5 rounded text-[6px] font-bold" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>hey</div>
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>arlo</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {/* Current subscription status */}
        {isSubscribed && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Crown className="h-5 w-5 text-emerald-400" />
                <div>
                  <p className="font-medium text-emerald-400">Pro Subscription Active</p>
                  <p className="text-sm text-muted-foreground">
                    {isCancelled 
                      ? `Cancels on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                      : `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {isCancelled ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReactivate}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Reactivate
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCancelDialog(true)}
                    disabled={loading}
                    className="text-muted-foreground"
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel subscription
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Hero */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-4"
          >
            {isSubscribed ? 'Manage Your Subscription' : 'Upgrade to Pro'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            {isSubscribed 
              ? 'You have access to all Pro features'
              : 'Unlock unlimited conversations, advanced AI modes, and more'
            }
          </motion.p>
        </div>

        {/* Billing toggle */}
        {!isSubscribed && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 p-1 bg-muted rounded-lg">
              <button
                onClick={() => setBillingInterval('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingInterval === 'monthly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('yearly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  billingInterval === 'yearly'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">
                  Save $38
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Pricing card */}
        {!isSubscribed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-md mx-auto"
          >
            <div className="relative bg-card border border-border rounded-2xl p-8 shadow-lg">
              {/* Popular badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-foreground text-background text-xs font-medium px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>

              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2">Pro</h2>
                <div className="flex items-baseline justify-center gap-1">
                  {appliedDiscount?.valid ? (
                    <>
                      <span className="text-2xl text-muted-foreground line-through">
                        ${billingInterval === 'yearly' ? '190' : '19'}
                      </span>
                      <span className="text-4xl font-bold text-emerald-500">
                        ${appliedDiscount.final_price}
                      </span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold">
                      ${billingInterval === 'yearly' ? '190' : '19'}
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    /{billingInterval === 'yearly' ? 'year' : 'month'}
                  </span>
                </div>
                {billingInterval === 'yearly' && !appliedDiscount?.valid && (
                  <p className="text-sm text-emerald-400 mt-1">
                    $15.83/month · 2 months free
                  </p>
                )}
                {appliedDiscount?.valid && (
                  <p className="text-sm text-emerald-400 mt-1">
                    You save ${appliedDiscount.discount_amount}!
                  </p>
                )}
              </div>

              {/* Discount Code Input */}
              <div className="mb-6">
                {appliedDiscount?.valid ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-emerald-500" />
                      <span className="font-mono font-medium text-emerald-500">{appliedDiscount.code}</span>
                      <span className="text-sm text-emerald-400">({appliedDiscount.description})</span>
                    </div>
                    <button onClick={removeDiscount} className="text-muted-foreground hover:text-foreground">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Discount code"
                      className="flex-1 font-mono"
                      onKeyDown={(e) => e.key === 'Enter' && validateDiscountCode()}
                      data-testid="discount-code-input"
                    />
                    <Button
                      variant="outline"
                      onClick={validateDiscountCode}
                      disabled={discountLoading || !discountCode.trim()}
                      data-testid="apply-discount-btn"
                    >
                      {discountLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSubscribe(billingInterval === 'yearly' ? 'pro_yearly' : 'pro_monthly')}
                disabled={loading || subscriptionLoading}
                className="w-full h-12 text-base"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Zap className="h-5 w-5 mr-2" />
                )}
                {loading ? 'Processing...' : appliedDiscount?.valid ? `Get Started - $${appliedDiscount.final_price}` : 'Get Started'}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Cancel anytime · Secure payment via Stripe
              </p>
            </div>
          </motion.div>
        )}

        {/* Features grid for subscribed users */}
        {isSubscribed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto"
          >
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 p-4 bg-card border border-border rounded-lg"
              >
                <Check className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </motion.div>
        )}

        {/* Secure payment notice */}
        <div className="mt-12 text-center">
          <p className="text-xs text-muted-foreground">
            Secure payment powered by Stripe
          </p>
        </div>
      </main>

      {/* Cancel Subscription Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-left">
              Your subscription will remain active until{' '}
              <span className="font-medium text-foreground">
                {subscription?.current_period_end 
                  ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })
                  : 'the end of your billing period'}
              </span>
              . After that, you will lose access to:
              <ul className="mt-3 space-y-1.5 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full" />
                  Unlimited conversations
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full" />
                  All thinking modes
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full" />
                  Knowledge base & voice input
                </li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-cancel-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PricingPage;
